// src/pages/LoginPage.js
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Logo from '../components/Logo';
import api, { AUTH_REQUEST_TIMEOUT_MS } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { getLoginErrorMessage } from '../utils/loginErrorMessage';
import { Button, Card, CardBody, FormControl, LayoutContainer, MessageBlock } from '../components/ui';
import '../styles/login.css';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [branchNumber, setBranchNumber] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submitLock = useRef(false);

  const [searchParams] = useSearchParams();
  const [message, setMessage] = useState('');

  useEffect(() => {
    const queryMessage = searchParams.get('message');
    if (queryMessage === 'sesion_expirada') {
      setMessage('Tu sesión ha expirado. Por favor, inicia sesión nuevamente.');
    }
  }, [searchParams]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (submitLock.current) {
      return;
    }

    submitLock.current = true;
    setIsSubmitting(true);
    setErrorMessage('');

    try {
      const response = await api.post(
        '/login',
        { username, password, branch_number: branchNumber },
        {
          skipAuth: true,
          skipUnauthorizedRedirect: true,
          timeout: AUTH_REQUEST_TIMEOUT_MS,
          networkRetry: true,
        }
      );

      const { access_token, user, commerce, config, role } = response.data;
      const { id: channelId, name: commerceName, logo: commerceLogo, branch } = commerce;
      const { id: branchId, name: branchName } = branch;

      login(access_token, user, channelId, branchId, commerceName, branchName, commerceLogo, config, role);

      navigate('/');
    } catch (error) {
      console.error('Login error:', error.response?.data || error.message);
      setErrorMessage(getLoginErrorMessage(error));
    } finally {
      submitLock.current = false;
      setIsSubmitting(false);
    }
  };  

  return (
    <div id="auth-left" className="rg-login">
      <LayoutContainer size="md" className="rg-login__container">
        <div className="auth-logo rg-login__logo">
          <Logo />
        </div>

        <Card className="rg-login__card">
          <CardBody className="rg-login__card-body">
            <h1 className="auth-title rg-login__title">Portal Comercios</h1>

            <div className="rg-spacing-stack rg-login__alerts">
              {errorMessage && (
                <MessageBlock tone="error" title="Error de acceso">
                  {errorMessage}
                </MessageBlock>
              )}
              {message && (
                <MessageBlock tone="warning" title="Sesion">
                  {message}
                </MessageBlock>
              )}
            </div>

            <form onSubmit={handleLogin} className="rg-login__form rg-spacing-stack" noValidate aria-busy={isSubmitting}>
              <FormControl
                id="branch-number"
                label="Numero de sucursal"
                type="text"
                placeholder="Numero de sucursal"
                value={branchNumber}
                onChange={(e) => setBranchNumber(e.target.value)}
                iconLeft={<i className="bi bi-shop" aria-hidden="true"></i>}
                autoComplete="organization"
                disabled={isSubmitting}
              />

              <FormControl
                id="username"
                label="Usuario"
                type="text"
                placeholder="Usuario"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                iconLeft={<i className="bi bi-person" aria-hidden="true"></i>}
                autoComplete="username"
                disabled={isSubmitting}
              />

              <FormControl
                id="password"
                label="Contraseña"
                type={showPassword ? 'text' : 'password'}
                placeholder="Contraseña"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                iconLeft={<i className="bi bi-shield-lock" aria-hidden="true"></i>}
                iconRightInteractive
                disabled={isSubmitting}
                iconRight={(
                  <button
                    type="button"
                    className="rg-login__password-toggle"
                    onClick={() => setShowPassword((prev) => !prev)}
                    aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                    title={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                    disabled={isSubmitting}
                  >
                    <i
                      className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`}
                      aria-hidden="true"
                    ></i>
                  </button>
                )}
                autoComplete="current-password"
              />

              <div className="rg-login__remember">
                <input
                  className="form-check-input rg-focusable"
                  type="checkbox"
                  checked={rememberMe}
                  onChange={() => setRememberMe(!rememberMe)}
                  id="remember-me"
                  disabled={isSubmitting}
                />
                <label className="form-check-label" htmlFor="remember-me">
                  Recordarme en esta computadora
                </label>
              </div>

              <Button type="submit" variant="primary" size="lg" fullWidth disabled={isSubmitting}>
                {isSubmitting ? 'Ingresando...' : 'Ingresar'}
              </Button>
            </form>
          </CardBody>
        </Card>
      </LayoutContainer>
    </div>
  );
};

export default LoginPage;
